/**
 * MSW Visit Note — static options + empty form state.
 * Common header (1–6) lives in mswNotesCommonHeaderUtils / MswNotesCommonHeader.
 * Fields below are visit-specific clinical sections (no demographics/insurance
 * duplicates already covered by the common header).
 */

import { emptyMswCommonHeader } from "./mswNotesCommonHeaderUtils";

export const COPING_MECHANISMS_OPTIONS = [
  "Religion/Spirituality",
  "Denial/Avoidance",
  "Family Support",
  "Rationalization",
];

export const SOCIAL_HISTORY_DYNAMICS_OPTIONS = [
  "Stable family relationships",
  "Recent bereavement (6 mo)",
  "Social isolation",
  "History of independence",
];

export const MENTAL_STATUS_OPTIONS = [
  "Alert/Oriented x3",
  "Confused",
  "Forgetful",
  "Disoriented",
];

export const MOOD_EMOTIONAL_OPTIONS = [
  "Anxious",
  "Flat Affect",
  "Cooperative",
  "Depressed",
];

export const RESIDENCE_TYPE_OPTIONS = [
  "Private Home",
  "Apartment",
  "ALF",
  "SNF",
  "Other",
];

export const HOME_SAFETY_OPTIONS = ["Safe", "Unsafe", "Barriers Identified"];

export const CAREGIVER_AVAILABILITY_OPTIONS = [
  "24/7 Assistance",
  "Daytime Only",
  "Intermittent",
  "None/Unavailable",
  "Other",
];

export const ABILITY_TO_ASSIST_OPTIONS = [
  "Competent",
  "Needs Training",
  "Limited",
  "Unable",
  "Other",
];

export const STRESS_STRAIN_OPTIONS = [
  "None Identified",
  "Mild Strain",
  "Moderate Strain Identified",
  "Severe Strain",
  "Other",
];

export const COMMUNITY_RESOURCES_OPTIONS = [
  "Meals on Wheels",
  "Transportation Svcs",
  "Support Groups",
  "Utility Assistance",
  "Legal Aid",
  "Mental Health Clinic",
];

export const ADVANCE_DIRECTIVE_YES_NO = ["Yes", "No"];

export const ADVANCE_DIRECTIVE_ROWS = [
  { key: "dnr", label: "DNR" },
  { key: "livingWill", label: "Living Will" },
  { key: "medicalPoa", label: "Medical POA" },
];

export const PROGRESS_GOAL_STATUS_OPTIONS = [
  "Met",
  "In Progress",
  "Plateau",
  "Not Met",
  "Discontinued",
];

/** Colored text for selected progress-goal status. */
export const PROGRESS_GOAL_STATUS_CLASS = {
  Met: "text-emerald-700 font-semibold",
  "In Progress": "text-sky-700 font-semibold",
  Plateau: "text-amber-700 font-semibold",
  "Not Met": "text-red-700 font-semibold",
  Discontinued: "text-slate-500 font-semibold",
};

export const INTERVENTIONS_PROVIDED_OPTIONS = [
  "Counseling",
  "Resource Coordination",
  "Crisis Intervention",
  "Advocacy",
  "Discharge Planning",
  "Grief Counseling",
  "Caregiver Support",
  "Other Intervention",
];

export const MSW_VISIT_ARRAY_KEYS = [
  "copingMechanisms",
  "socialHistoryDynamics",
  "mentalStatus",
  "moodEmotional",
  "communityResources",
  "interventionsProvided",
];

const newItemId = (prefix = "item") =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyProgressGoal = (n = 1) => ({
  id: newItemId(`goal-${n}`),
  description: "",
  status: "",
  details: "",
});

export const emptyProgressGoals = (count = 1) =>
  Array.from({ length: Math.max(1, count) }, (_, i) =>
    emptyProgressGoal(i + 1),
  );

export const renumberProgressGoals = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    id: row?.id || newItemId(`goal-${idx + 1}`),
    description:
      typeof row === "string" ? row : row?.description || row?.text || "",
    status: row?.status || "",
    details: row?.details || "",
  }));

export const normalizeProgressGoals = (rows, { minCount = 1 } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return emptyProgressGoals(minCount);
  return renumberProgressGoals(list);
};

export const serializeProgressGoals = (rows) =>
  renumberProgressGoals(rows).map(({ id, description, status, details }) => ({
    id,
    description: description || "",
    status: status || "",
    details: details || "",
  }));

export const addProgressGoal = (rows) => {
  const current = Array.isArray(rows) ? rows : [];
  return renumberProgressGoals([
    ...current,
    emptyProgressGoal(current.length + 1),
  ]);
};

export const removeProgressGoal = (rows, goalId, { minCount = 1 } = {}) => {
  const current = Array.isArray(rows) ? rows : [];
  if (current.length <= minCount) return current;
  return renumberProgressGoals(current.filter((row) => row.id !== goalId));
};

export const emptyAdvanceDirectives = () => ({
  dnr: "",
  livingWill: "",
  medicalPoa: "",
});

/** Normalize advance-directive values to "yes" | "no" | "". */
export const normalizeAdvanceDirectives = (value) => {
  const src = value && typeof value === "object" ? value : {};
  const toYn = (v) => {
    const s = String(v || "")
      .trim()
      .toLowerCase();
    if (s === "yes" || s === "y") return "yes";
    if (s === "no" || s === "n") return "no";
    return "";
  };
  return {
    dnr: toYn(src.dnr),
    livingWill: toYn(src.livingWill),
    medicalPoa: toYn(src.medicalPoa),
  };
};

/** Map stored yes/no → ExclusiveChoiceGrid display label. */
export const advanceDirectiveDisplay = (stored) => {
  if (stored === "yes") return "Yes";
  if (stored === "no") return "No";
  return "";
};

/** Map ExclusiveChoiceGrid Yes/No → stored yes/no. */
export const advanceDirectiveFromDisplay = (label) => {
  if (label === "Yes") return "yes";
  if (label === "No") return "no";
  return "";
};

export const toggleInList = (list, item) => {
  const current = Array.isArray(list) ? list : [];
  return current.includes(item)
    ? current.filter((x) => x !== item)
    : [...current, item];
};

export const emptyMswVisitPayload = () => ({
  commonHeader: emptyMswCommonHeader(),
  reasonForVisit: "",
  copingMechanisms: [],
  socialHistoryDynamics: [],
  mentalStatus: [],
  moodEmotional: [],
  residenceType: "",
  livesWith: "",
  homeSafetyConcerns: "",
  caregiverAvailability: "",
  abilityToAssist: "",
  stressStrainIndicators: "",
  financialBarriersToCare: "",
  communityResources: [],
  advanceDirectives: emptyAdvanceDirectives(),
  patientCaregiverEducation: "",
  progressGoals: emptyProgressGoals(1),
  interventionsProvided: [],
  planFrequency: "",
  nextScheduledDate: "",
  additionalNotes: "",
  billingCode: "",
  timeIn: "",
  timeOut: "",
});
